/**
 * WebAudio synthesis. No sample files, no loading, no asset pipeline — every
 * sound is an oscillator and/or filtered noise shaped by an envelope.
 *
 * Three things this is careful about, all learned from listening to it:
 *  - A compressor on the master. Cascades fire match + row + block + energy
 *    cues in the same frame; without a limiter they sum past unity and the
 *    best moment in the game is the one that distorts.
 *  - One shared noise buffer, played from a random offset. Allocating and
 *    filling a fresh buffer per cue caused audible GC hitches mid-cascade.
 *  - A voice cap. A deep cascade used to spawn dozens of simultaneous
 *    oscillators, which sounded less like a jackpot and more like a crash.
 */

export interface Sfx {
  ctx: AudioContext | null;
  master: GainNode | null;
  compressor: DynamicsCompressorNode | null;
  muted: boolean;
  /** Active oscillator/buffer voices. Capped so a cascade cannot snowball. */
  voices: number;
  volume: number;
}

const CASCADE_BASE = 220;
const SEMITONES = [0, 3, 7, 10, 12, 15, 19, 22, 24];
const MAX_VOICES = 24;
const NOISE_SECONDS = 2;

/** Shared across all cues; filled once on first unlock. */
let noiseBuffer: AudioBuffer | null = null;

export function createSfx(): Sfx {
  return { ctx: null, master: null, compressor: null, muted: false, voices: 0, volume: 0.5 };
}

/** Browsers require a gesture before audio. Call from the first pointer/key. */
export function unlock(sfx: Sfx): void {
  if (sfx.ctx) {
    // A context can still be suspended after creation; resume is a no-op when
    // it is already running, so this is always safe to call.
    if (sfx.ctx.state === 'suspended') void sfx.ctx.resume();
    return;
  }
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;

  const ctx = new Ctor();

  // Master gain → compressor → destination. The compressor is the difference
  // between a big cascade feeling powerful and feeling broken.
  const master = ctx.createGain();
  master.gain.value = sfx.muted ? 0 : sfx.volume;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -14;
  compressor.knee.value = 24;
  compressor.ratio.value = 8;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.18;

  master.connect(compressor);
  compressor.connect(ctx.destination);

  sfx.ctx = ctx;
  sfx.master = master;
  sfx.compressor = compressor;

  // Some browsers leave a freshly constructed context suspended even inside a
  // gesture. Resume explicitly so the first cue is not silently swallowed.
  if (ctx.state === 'suspended') void ctx.resume();
}

export function setMuted(sfx: Sfx, muted: boolean): void {
  sfx.muted = muted;
  if (sfx.master && sfx.ctx) {
    sfx.master.gain.setTargetAtTime(muted ? 0 : sfx.volume, sfx.ctx.currentTime, 0.02);
  }
}

export function setVolume(sfx: Sfx, volume: number): void {
  sfx.volume = Math.max(0, Math.min(1, volume));
  if (!sfx.muted && sfx.master && sfx.ctx) {
    sfx.master.gain.setTargetAtTime(sfx.volume, sfx.ctx.currentTime, 0.02);
  }
}

function ready(sfx: Sfx): sfx is Sfx & { ctx: AudioContext; master: GainNode } {
  return sfx.ctx !== null && sfx.master !== null && !sfx.muted && sfx.voices < MAX_VOICES;
}

/** Track a voice until its source ends, so the cap cannot leak. */
function hold(sfx: Sfx, stopAt: number): void {
  sfx.voices += 1;
  const release = Math.max(0, (stopAt - (sfx.ctx?.currentTime ?? 0)) * 1000 + 40);
  setTimeout(() => {
    sfx.voices = Math.max(0, sfx.voices - 1);
  }, release);
}

function sharedNoise(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  const frames = Math.max(1, Math.floor(ctx.sampleRate * NOISE_SECONDS));
  const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buf;
  return buf;
}

interface ToneOpts {
  freq: number;
  to?: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
  /** Lowpass ceiling on the voice. Keeps square/saw from becoming piercing. */
  cutoff?: number;
}

function tone(sfx: Sfx, o: ToneOpts): void {
  if (!ready(sfx)) return;
  const { ctx, master } = sfx;
  const t0 = ctx.currentTime + (o.delay ?? 0);

  const osc = ctx.createOscillator();
  osc.type = o.type ?? 'triangle';
  osc.frequency.setValueAtTime(o.freq, t0);
  if (o.to !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t0 + o.dur);

  const g = ctx.createGain();
  const peak = o.gain ?? 0.14;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);

  // Every voice gets a lowpass. A raw square wave at these gains is a needle.
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = o.cutoff ?? 2600;

  osc.connect(lp);
  lp.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + o.dur + 0.02);
  hold(sfx, t0 + o.dur);
}

interface NoiseOpts {
  dur: number;
  gain: number;
  cutoff: number;
  q?: number;
  delay?: number;
  /** Sweep the filter down this much over the cue. 1 = to 25% of cutoff. */
  sweep?: number;
}

function noise(sfx: Sfx, o: NoiseOpts): void {
  if (!ready(sfx)) return;
  const { ctx, master } = sfx;
  const t0 = ctx.currentTime + (o.delay ?? 0);

  const src = ctx.createBufferSource();
  src.buffer = sharedNoise(ctx);
  // Random offset so repeated cues do not sound like the same clip looping.
  const offset = Math.random() * (NOISE_SECONDS - o.dur - 0.05);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(o.cutoff, t0);
  filter.frequency.exponentialRampToValueAtTime(
    Math.max(80, o.cutoff * (o.sweep ?? 0.25)),
    t0 + o.dur,
  );
  filter.Q.value = o.q ?? 1;

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(o.gain, t0 + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);

  src.connect(filter);
  filter.connect(g);
  g.connect(master);
  src.start(t0, Math.max(0, offset), o.dur + 0.05);
  hold(sfx, t0 + o.dur);
}

// --- cues -----------------------------------------------------------------

/** Piece locking into the well. */
export function playLock(sfx: Sfx): void {
  tone(sfx, { freq: 150, to: 70, dur: 0.1, type: 'triangle', gain: 0.2, cutoff: 1400 });
  noise(sfx, { dur: 0.05, gain: 0.09, cutoff: 1400 });
}

/**
 * A match detonating. Pitch climbs with cascade depth, and so does the
 * richness — wave 1 is a clean pluck, wave 3 has an octave shimmer on top.
 * That widening is most of what sells a chain better than pitch alone.
 */
export function playMatch(sfx: Sfx, wave: number): void {
  const step = SEMITONES[Math.min(wave - 1, SEMITONES.length - 1)] ?? 0;
  const f = CASCADE_BASE * Math.pow(2, step / 12);
  const depth = Math.min(wave, 4);

  tone(sfx, { freq: f, to: f * 1.5, dur: 0.11 + depth * 0.015, type: 'triangle', gain: 0.15 });
  tone(sfx, { freq: f * 2, dur: 0.055, type: 'sine', gain: 0.06 + depth * 0.012 });

  // The octave shimmer only arrives once the chain is actually deep.
  if (wave >= 3) {
    tone(sfx, { freq: f * 3, dur: 0.07, type: 'sine', gain: 0.045, delay: 0.02, cutoff: 5200 });
  }
  if (wave >= 4) {
    tone(sfx, { freq: f * 4, dur: 0.09, type: 'sine', gain: 0.035, delay: 0.045, cutoff: 6000 });
  }

  noise(sfx, { dur: 0.07, gain: 0.06, cutoff: 3200 });
}

/** Full row(s) clearing. Heavier with each extra row. */
export function playRowClear(sfx: Sfx, rows: number): void {
  noise(sfx, { dur: 0.24 + rows * 0.05, gain: 0.15, cutoff: 2600, q: 0.8, sweep: 0.18 });
  tone(sfx, { freq: 90, to: 40, dur: 0.3, type: 'sawtooth', gain: 0.15, cutoff: 900 });
  if (rows >= 2) {
    tone(sfx, { freq: 130, to: 55, dur: 0.34, type: 'triangle', gain: 0.09, delay: 0.04, cutoff: 800 });
  }
}

/** Column topped out. Unpleasant on purpose. */
export function playCap(sfx: Sfx): void {
  tone(sfx, { freq: 180, to: 55, dur: 0.4, type: 'sawtooth', gain: 0.18, cutoff: 700 });
  noise(sfx, { dur: 0.3, gain: 0.13, cutoff: 900, q: 4 });
}

export function playCard(sfx: Sfx): void {
  noise(sfx, { dur: 0.045, gain: 0.07, cutoff: 5000 });
  tone(sfx, { freq: 660, to: 880, dur: 0.05, type: 'sine', gain: 0.05 });
}

export function playDraw(sfx: Sfx): void {
  noise(sfx, { dur: 0.04, gain: 0.045, cutoff: 6000 });
}

export function playEnemyHit(sfx: Sfx): void {
  tone(sfx, { freq: 240, to: 90, dur: 0.12, type: 'triangle', gain: 0.14, cutoff: 1800 });
  noise(sfx, { dur: 0.09, gain: 0.09, cutoff: 2200 });
}

export function playPlayerHit(sfx: Sfx): void {
  tone(sfx, { freq: 160, to: 60, dur: 0.2, type: 'sawtooth', gain: 0.16, cutoff: 800 });
  noise(sfx, { dur: 0.14, gain: 0.1, cutoff: 1200, q: 2 });
}

export function playBlock(sfx: Sfx): void {
  tone(sfx, { freq: 420, to: 620, dur: 0.09, type: 'triangle', gain: 0.08 });
}

export function playEnergy(sfx: Sfx): void {
  tone(sfx, { freq: 520, to: 780, dur: 0.1, type: 'sine', gain: 0.09 });
}

export function playStone(sfx: Sfx): void {
  tone(sfx, { freq: 70, to: 45, dur: 0.16, type: 'triangle', gain: 0.15, cutoff: 600 });
  noise(sfx, { dur: 0.12, gain: 0.08, cutoff: 700, q: 3 });
}

export function playWin(sfx: Sfx): void {
  const major = [0, 4, 7, 12];
  for (let i = 0; i < major.length; i++) {
    tone(sfx, {
      freq: 330 * Math.pow(2, (major[i] ?? 0) / 12),
      dur: 0.22,
      type: 'triangle',
      gain: 0.11,
      delay: i * 0.11,
    });
  }
}

export function playLose(sfx: Sfx): void {
  for (let i = 0; i < 3; i++) {
    tone(sfx, {
      freq: 220 * Math.pow(2, -(i * 3) / 12),
      dur: 0.3,
      type: 'sawtooth',
      gain: 0.11,
      delay: i * 0.16,
      cutoff: 900,
    });
  }
}
